import { FC, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getContractTradeType, contractsUIConfig } from "@/utils/Contracts";
import { useTranslation } from "react-i18next";
import { Contract } from "@/types";
import { postProcessOrder } from "@/services/Orders";

import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { useAuth } from "@/contexts/AuthContext";
import { useTradeLock } from "@/hooks/useTradeLock";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import CTA from "@/components/atoms/CTA";
import OrderStatusEnum from "@/constants/enums/OrderStatusEnum";
import { useOrdersDrawerStore } from "@/stores/ordersDrawerStore";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import Table from "@/components/atoms/Table";
import SortableHeader from "@/components/molecules/SortableHeader";
import styles from "./styles.module.css";
import React from "react";
import { Menu } from "@mantine/core";
import Group from "@/components/atoms/Group";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import dayjs from "dayjs";
import { useGetCompanyByIdPublic } from "@/services/Company";

interface Props {
  contract: Contract;
}

const ContractsTable: FC<Props> = ({ contract }: Props) => {
  const { t } = useTranslation();
  const [isMutatingRow, setIsMutatingRow] = useState<Set<number>>(new Set<number>());
  const [sorting, setSorting] = useState<SortingState>([]);

  const { isViewer, currentCompany } = useAuth();
  const { guardTradeLock } = useTradeLock();
  const queryClient = useQueryClient();
  const contractTradeType = getContractTradeType(contract, currentCompany);
  const { tableIconName } = contractsUIConfig(contractTradeType);

  const openOrdersDrawer = useOrdersDrawerStore((s) => s.open);

  const { mutateAsync: mutateProcessOrder } = useMutation({
    mutationFn: async (orderId: number) => {
      setIsMutatingRow((prev) => new Set(prev).add(orderId));
      await postProcessOrder({
        orderId: orderId,
      });
    },
    onSettled: (data, error, orderId) => {
      setIsMutatingRow((prev) => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getContractById", contract.id],
      });
    },
  });

  // Memoised and listed in the column defs' dependency array below, so the guard cannot go stale.
  const processOrder = useMemo(() => guardTradeLock(mutateProcessOrder), [guardTradeLock, mutateProcessOrder]);

  function openDrawer(index: number) {
    const orderId = ordersOnContract[index].id;
    openOrdersDrawer({ orderId });
  }

  const ordersOnContract =
    contractTradeType === ContractTradeTypeEnum.SELL ? contract.salesOrdersOnContract : contract.buyOrdersOnContract;

  const columns: ColumnDef<any>[] = useMemo(
    () => [
      {
        header: ({ column }) => <NonSortableHeader column={column} label={""} />,
        accessorKey: "orderType",
        cell: () => <SVGAsset name={tableIconName} />,
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:orderNumber")} />,
        accessorKey: "id",
        cell: ({ row }) => <div style={{ justifySelf: "center" }}>{row.original.id}</div>,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:date")} />,
        accessorKey: "created",
        cell: ({ row }) => dayjs(row.original.created).format("DD.MM.YYYY"),
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("product:product")} />,
        accessorKey: "product",
        cell: ({ row }) => {
          if (row.original.orderProduct) {
            return row.original.orderProduct;
          } else if (row.original.companySpecificProductDto) {
            return row.original.companySpecificProductDto.companySpecificProductName;
          } else {
            return row.original.salesItem.salesItem + " " + row.original.condition?.condition;
          }
        },
        meta: {
          breakpoint: "",
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:type")} />,
        accessorKey: "salesItem.salesItem",
        cell: ({ row }) => row.original.salesItem.salesItem,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:amount")} />,
        accessorKey: "finalAmount",
        cell: ({ row }) =>
          `${row.original.finalAmount !== null ? row.original.finalAmount : row.original.amount} ${row.original.unit.unit}`,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:price")} />,
        accessorKey: "price",
        cell: ({ row }) => `${row.original.price?.NOK} ${row.original.orderCurrency}/${row.original.priceUnit?.unit}`,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={"Status"} />,
        accessorKey: "status",
        cell: ({ row }) => `${t("enumerables:orderStatus." + row.original.status.toUpperCase())}`,
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("tasks:counterParty")} />,
        accessorKey: "counterpart",
        cell: ({ row }) => {
          const counterpart = row.original.companiesToTradeWith.filter(
            (company: number) => company !== currentCompany?.companyId
          )[0];
          if (counterpart) {
            return <CounterpartyCell counterpartCompanyId={counterpart} />;
          } else {
            return "-";
          }
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:fullLoad")} />,
        accessorKey: "fullLoad",
        cell: ({ row }) => <SVGAsset name={row.original.fullLoad ? "checkmark-green" : "forbidden-red"} />,
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={""} />,
        accessorKey: "actions",
        cell: ({ row }) => {
          if (
            contractTradeType === ContractTradeTypeEnum.BUY &&
            row.original.status === OrderStatusEnum.AWAITING_PROCESSING
          ) {
            return (
              <CTA
                loading={isMutatingRow.has(row.original.id)}
                disabled={isViewer(currentCompany)}
                onClick={(e) => {
                  e.stopPropagation();
                  processOrder(row.original.id);
                }}
              >
                {t("orders:processOrder")}
              </CTA>
            );
          }
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={""} />,
        accessorKey: "menu",
        cell: ({ row }) => {
          return (
            contractTradeType === ContractTradeTypeEnum.BUY && (
              <Menu offset={-10}>
                <Menu.Target>
                  <td align="right" style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    <SVGAsset name="dotdotdot-grey" />
                  </td>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    style={{ width: "auto" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      openOrdersDrawer({ orderId: row.original.id, tab: "claims" });
                    }}
                  >
                    <Group>
                      <SVGAsset name="yellow-exclamation" />
                      {t("orders:reportProblem")}
                    </Group>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )
          );
        },
      },
    ],
    [currentCompany?.companyId, t, isMutatingRow, processOrder]
  );

  const table = useReactTable({
    data: ordersOnContract ? ordersOnContract : [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <Table className={styles.tableRoot}>
        <Table.Thead>
          {table.getHeaderGroups().map((headerGroup) => {
            return (
              <Table.Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.Th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</Table.Th>
                ))}
              </Table.Tr>
            );
          })}
        </Table.Thead>
        <Table.Tbody>
          {table.getRowModel().rows.map((row) => (
            <Table.Tr key={row.id} className={styles.tableRow} onClick={() => openDrawer(row.index)}>
              {row.getVisibleCells().map((cell) => (
                <Table.Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </>
  );
};

export default ContractsTable;

const CounterpartyCell = ({ counterpartCompanyId }: { counterpartCompanyId: number }) => {
  const { data: counterpart } = useGetCompanyByIdPublic(counterpartCompanyId);

  return counterpart?.companyName;
};
